import { findCouncil } from '@curby/shared';
import { Flex, Text } from '@mantine/core';
import { IconHomeCheck, IconHomeExclamation } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InfoAlert, WarningAlert } from '../alerts';
import { DropPointsMap } from '../drop-locations-map';
import { useGetDropOffLocationsQuery } from './graphql.generated';
import styles from './styles.module.css';

type AddressCheckProps = {
	council: string | null | undefined;
	postcode: string | null | undefined;
	state: string | null | undefined;
	lat: number;
	lng: number;
};

export const AddressCheck = ({ council, postcode, state, lat, lng }: AddressCheckProps) => {
	const [matchedCouncil, setMatchedCouncil] = useState<string | undefined>(undefined);
	const [showDropOffPoints, setShowDropOffPoints] = useState(false);
	const { data: locations, loading } = useGetDropOffLocationsQuery({
		variables: {
			input: {
				lat,
				lng,
			},
		},
		skip: !lat || !lng, // Skip query if lat/lng not available
		fetchPolicy: 'cache-and-network', // Ensure fresh data when variables change
		notifyOnNetworkStatusChange: true, // Update loading state on refetch
	});

	const locationsData = useMemo(() => {
		if (loading) {
			return {
				status: 'loading',
				locations: [],
				nearestLocation: null,
			};
		}

		if (locations?.getDropOffLocations?.length === 0) {
			return {
				status: 'out_of_range',
				locations: [],
			};
		}

		const nearestLocation = locations?.getDropOffLocations
			?.filter((l) => l?.drivingDistance && l.drivingDistance < 200)
			.sort((a, b) => (a?.drivingDistance ?? 0) - (b?.drivingDistance ?? 0))[0];

		return {
			status:
				nearestLocation?.drivingDistance && nearestLocation.drivingDistance <= 50
					? 'in_range'
					: 'out_of_range',
			locations: locations?.getDropOffLocations,
		};
	}, [loading, locations]);

	useEffect(() => {
		setMatchedCouncil(findCouncil(council, state, postcode));
	}, [council, state, postcode]);

	const titleText = useMemo(() => {
		if (matchedCouncil) {
			return `Your address is in ${matchedCouncil} and is eligible for kerbside pick-up.`;
		} else {
			switch (locationsData.status) {
				case 'loading':
					return 'Calculating your options...';
				case 'in_range':
					return 'Good news! There are drop-off points in your area:';
				case 'out_of_range':
					return 'There are no drop-off points in your area.';
			}
		}
	}, [locationsData, matchedCouncil]);

	const AlertWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => {
			if (matchedCouncil || ['in_range', 'loading'].includes(locationsData.status)) {
				return (
					<InfoAlert title={titleText} c="black" icon={<IconHomeCheck name="info" />}>
						{children}
					</InfoAlert>
				);
			} else {
				return (
					<WarningAlert title={titleText} icon={<IconHomeExclamation />}>
						{children}
					</WarningAlert>
				);
			}
		},
		[matchedCouncil, locationsData, titleText]
	);

	if (!(council && state) && !postcode) {
		return null;
	}

	return (
		<>
			<AlertWrapper>
				{matchedCouncil && (
					<Text size="xs" fw={400}>
						Please place your bag in the yellow bin. Alternatively you may place your bag at your
						closest drop off point
					</Text>
				)}
				{!matchedCouncil && locationsData.locations && locationsData.locations.length > 0 && (
					<Flex direction="column" c="black" gap="14px">
						<Flex direction="column" gap="6px">
							<Text size="14px" fw={600}>
								Your nearest is:
							</Text>
							<Text key={locationsData.locations?.[0]?.id} size="14px" fw={400}>
								{`${locationsData.locations?.[0]?.retailer} - ${locationsData.locations?.[0]?.name} (${locationsData.locations?.[0]?.drivingDistance?.toFixed(1)}km)`}
							</Text>
							<a
								href={`https://www.google.com/maps/search/?api=1&query=${locationsData.locations?.[0]?.lat},${locationsData.locations?.[0]?.lng}`}
								target="_blank"
								rel="noreferrer"
								className={styles.link}
							>
								{locationsData.locations?.[0]?.address}
							</a>
						</Flex>
						<Flex direction="column" gap="4px">
							<Text size="14px" fw={600}>
								Other options:
							</Text>
							{locationsData.locations.slice(1).map((loc) => (
								<Text key={loc?.id} size="14px" fw={400}>
									{`${loc?.retailer} - ${loc?.name} (${loc?.drivingDistance?.toFixed(1)}km)`}
								</Text>
							))}
						</Flex>
						<button
							type="button"
							onClick={() => setShowDropOffPoints(true)}
							className={styles.stringButton}
						>
							Show drop off points
						</button>
					</Flex>
				)}
			</AlertWrapper>
			<DropPointsMap open={showDropOffPoints} setOpen={setShowDropOffPoints} />
		</>
	);
};
